import {
  Component,
  OnInit,
  ViewEncapsulation,
  OnDestroy,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import {
  McAutocompleteComponent,
  MCButtonComponent,
  MCDialogComponent,
  MCFormFieldWrapperComponent,
  McInfoComponent,
} from '@maslul/common-components';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteModule } from 'primeng/autocomplete';
import {
  MCCodeListWrapperService,
  MCDialog,
  MCDialogButton,
  MCDialogFooter,
  MCDialogHeader,
  MCSelectItemNested,
  MCUtils,
} from '@maslul/common-services';
import {
  RequestApprovalTypeDetails,
  RequestApprovalTypeDetailsStore,
} from '../../../../../services/store/approval-type-details-store';
import { Subscription } from 'rxjs';
import { ValidationMessageData } from '../../../../../resources/validation-massage';
import { ApprovalTypeService } from '../../../../../services/pages/approval-type/approval-type.service';
import { TranslateService } from '@ngx-translate/core';
import { SecondRequest } from '../../../../../models/request-submission-methods/approvalType/secondRequest';
import { FocusOnTabDirective } from '../../../../../services/shared/directives/focus-on-tab';
import { ApprovalTypesService } from '../../../../../services/approval-type/approval-type';
import { RequestSubmissionMethodsSkeletonComponent } from '../../components/request-submission-methods/skeleton/skeleton';
import { PageBaseComponent } from '../../../../shared/page-base';
import { RequestService } from '../../../../../services/request/request';
import { GeneralStore } from '../../../../../services/store/general';
import { GeneralDetailsStore } from '../../../../../services/store/general-details';
import { ProgressService } from '../../../../../services/shared/progress';
import { Router } from '@angular/router';

@Component({
  selector: 'mi-request-submission-methods',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MCButtonComponent,
    McAutocompleteComponent,
    AutoCompleteModule,
    McInfoComponent,
    InputTextModule,
    MCFormFieldWrapperComponent,
    RequestSubmissionMethodsSkeletonComponent,
    FocusOnTabDirective,
    MCDialogComponent,
  ],
  providers: [],
  templateUrl: './request-submission-methods.html',
  styleUrl: './request-submission-methods.scss',
  encapsulation: ViewEncapsulation.None,
})
//todo: use base
export class RequestSubmissionMethodsComponent
  extends PageBaseComponent
  implements OnInit, OnDestroy {
  private translateService = inject(TranslateService);
  private codeListService = inject(MCCodeListWrapperService);
  private approvalTypeService = inject(ApprovalTypeService);
  private requestApprovalTypeDetailsStore = inject(
    RequestApprovalTypeDetailsStore,
  );
  private approvalTypesService = inject(ApprovalTypesService);
  private progressService = inject(ProgressService);
  private requestService = inject(RequestService);
  private generalStore = inject(GeneralStore);
  private generalDetailsStore = inject(GeneralDetailsStore);

  authoritySearchQuery: string = '';
  public customErrorMessages = ValidationMessageData.customErrorMessages;
  genericText = toSignal(this.translateService.stream('generic'));
  requestSubmissionMethodsText = toSignal(
    this.translateService.stream('requestSubmissionMethods'),
  );
  dialogProperties: MCDialog = new MCDialog();

  authorities: MCSelectItemNested[] = [];
  authorityInfo: string | undefined;
  filteredAuthorities: MCSelectItemNested[] = [];
  selectedAuthority: MCSelectItemNested | undefined;
  subscription: Subscription = new Subscription();
  endedLoading: boolean = false;
  isSentRequest: boolean = false;

  ngOnInit(): void {
    this.init();
  }

  init() {
    this.initSubscriptions();
    this.initDialog();
  }

  loadRequestDataForExistingRequest() {
    this.initAuthoritySelected();
    this.authoritySelected();
  }

  initSubscriptions() {
    this.subscription.add(
      this.codeListService
        .getAuthorityWithApproval(false)
        .subscribe((authoritiesWithApproval: MCSelectItemNested[]) => {
          this.authorities = authoritiesWithApproval;
          this.filteredAuthorities = this.authorities;
          this.initAuthoritySelected();
          if (!this.isNewRequest && this.requestId) {
            this.loadRequestDataForExistingRequest();
          }
          this.endedLoading = true;
        }),
    );
    this.subscription.add(
      this.generalStore
        .getIsSent()
        .subscribe((isSent) => (this.isSentRequest = isSent)),
    );
  }

  selectSingelAuthority() {
    if (this.authorities?.length == 1) {
      this.selectedAuthority = this.authorities[0];
      this.authoritySelected();
    }
  }

  initAuthoritySelected() {
    const existingAuthorityId =
      this.requestApprovalTypeDetailsStore.getSnapshot().authorityId;
    if (existingAuthorityId) {
      const found = this.authorities.find(
        (auth) => auth.value == existingAuthorityId,
      );
      this.selectedAuthority = found;
      this.setAuthorityInfo();
    } else {
      this.selectSingelAuthority();
    }
  }

  searchAuthorities(event: any) {
    const filteredAuthorities: MCSelectItemNested[] = [];
    this.authoritySearchQuery = event.query.toLowerCase();

    for (let i = 0; i < this.authorities.length; i++) {
      const authority = this.authorities[i];
      if (authority.label?.toLowerCase().includes(this.authoritySearchQuery)) {
        filteredAuthorities.push(authority);
      }
    }

    this.filteredAuthorities = filteredAuthorities;
  }
  initDialog() {
    this.dialogProperties.breakpoints = {
      '960px': '75vw', // ברוחב 960px ומטה, רוחב הדיאלוג יהיה 75vw
      '640px': '80vw', // ברוחב 640px ומטה, רוחב הדיאלוג יהיה 80
    };
    this.dialogProperties.id = 'mi-change-authority';
  }
  showDialogOnAuthoritySelectedChange(
    dialogProperties: MCDialog,
    dialogText: any,
    authorityIdFromStore: string,
  ) {
    dialogProperties.visible = true;
    dialogProperties.header = new MCDialogHeader();
    dialogProperties.header.closeButton = new MCDialogButton();
    dialogProperties.header.closeButton.label = 'X';
    dialogProperties.header.closeButton.event = () => {
      this.changeAuthoritySelectedCancle(
        dialogProperties,
        authorityIdFromStore,
      );
    };
    dialogProperties.footer = new MCDialogFooter();
    dialogProperties.footer.button = new MCDialogButton();
    dialogProperties.footer.button.label = dialogText.okButton;
    dialogProperties.footer.button.event = () => {
      this.changeAuthoritySelectedOK(dialogProperties);
    };
    dialogProperties.footer.secondButton = new MCDialogButton();
    dialogProperties.footer.secondButton.label = dialogText.cancelButton;
    dialogProperties.footer.secondButton.class = 'mi-white-button';
    dialogProperties.footer.secondButton.event = () => {
      this.changeAuthoritySelectedCancle(
        dialogProperties,
        authorityIdFromStore,
      );
    };
  }

  changeAuthoritySelectedOK(dialogProperties: MCDialog) {
    this.approvalTypesService.clearStoreOnApprovalChange();
    this.progressService.resetTabs();
    this.authoritySelected();
    dialogProperties.visible = false;
  }

  changeAuthoritySelectedCancle(
    dialogProperties: MCDialog,
    authorityIdFromStore: string,
  ) {
    this.selectedAuthority = this.authorities.find(
      (item) => item.value?.toString() == authorityIdFromStore,
    );
    dialogProperties.visible = false;
  }

  onSelectAuthority() {
    const authorityIdFromStore =
      this.requestApprovalTypeDetailsStore.getSnapshot().authorityId;
    if (
      authorityIdFromStore &&
      this.selectedAuthority?.value.toString() !==
      authorityIdFromStore.toString()
    ) {
      this.showDialogOnAuthoritySelectedChange(
        this.dialogProperties,
        this.requestSubmissionMethodsText()?.dialogAutorityChanged,
        authorityIdFromStore,
      );
    } else {
      this.authoritySelected();
    }
  }

  authoritySelected() {
    this.setAuthorityAndMainTypeDetails();
    this.setAuthorityInfo();
  }

  getSecondApprovalTypes() {
    const mainTypes = this.selectedAuthority?.items || [];
    const allApprovalTypes: SecondRequest[] = [];
    mainTypes.forEach((mainType: any) => {
      const approvalTypes = mainType?.items || [];
      const mainTypeCode =
        mainType?.additionalColumns?.['Code'] || mainType?.additionalCode || '';
      approvalTypes.forEach((x: any) => {
        allApprovalTypes.push(
          new SecondRequest(
            x.value?.toString(),
            x.label,
            x.additionalColumns?.['MIApprovalTypeDescription'],
            x.additionalColumns?.['Secondary'],
            x.additionalColumns?.['CustomItem4Digits'] === 'True',
            mainType?.label,
            mainTypeCode,
          ),
        );
      });
    });
    return allApprovalTypes;
  }

  setAuthorityAndMainTypeDetails() {
    this.requestApprovalTypeDetailsStore.setAuthorityId(
      this.selectedAuthority?.value || '',
    );
    const mainTypes = this.selectedAuthority?.items || [];
    const secondApproval = this.getSecondApprovalTypes();
    this.requestApprovalTypeDetailsStore.setApprovalTypes(secondApproval || []);
    const firstApproval = secondApproval?.[0];
    this.requestApprovalTypeDetailsStore.setTypeCode(
      mainTypes[0]?.additionalColumns?.['Code'] ||
      mainTypes[0]?.additionalCode ||
      '',
    );
    this.requestApprovalTypeDetailsStore.setApprovalMainTypeName(
      firstApproval?.mainTypeName || mainTypes[0]?.label || '',
    );
  }

  setAuthorityInfo() {
    const requestApprovalTypeDetails: RequestApprovalTypeDetails =
      this.requestApprovalTypeDetailsStore.getSnapshot();
    const authorityKey = MCUtils.padZero(
      requestApprovalTypeDetails.authorityId,
    );
    this.approvalTypeService
      .getInfoByKey(authorityKey)
      .subscribe((info: string) => {
        this.authorityInfo = info;
      });
  }

  disabledChoosApprovalType(): boolean {
    return !this.selectedAuthority?.value;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public override next(): void {
    this.navigate('approval-type-method');
  }

  public override back(): void {
    throw new Error('Method not implemented.');
  }
}
